import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { ArrowRight, Stack, Sparkle, Crown, Eye, ShieldWarning, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import KundaliChart from "../components/KundaliChart";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import PaywallGate from "../components/PaywallGate";

const TAG_TINT = {
  kendra: { bg: "bg-amber-200", text: "text-amber-950" },
  trikona: { bg: "bg-emerald-200", text: "text-emerald-950" },
  dusthana: { bg: "bg-rose-200", text: "text-rose-950" },
  upachaya: { bg: "bg-indigo-200", text: "text-indigo-900" },
  maraka: { bg: "bg-orange-200", text: "text-orange-900" },
  neutral: { bg: "bg-slate-200", text: "text-slate-800" },
};

function InsightCard({ ins }) {
  const isTwin = ins.twin_activation;
  const isRaja = ins.rtn_house_tags.includes("kendra") || ins.rtn_house_tags.includes("trikona");
  const isDusthana = ins.rtn_house_tags.includes("dusthana");
  const bg = isTwin ? "from-amber-50 to-yellow-100" :
             isDusthana ? "from-rose-50 to-orange-50" :
             isRaja ? "from-emerald-50 to-teal-50" : "from-slate-50 to-white";
  const border = isTwin ? "border-amber-300" : isDusthana ? "border-rose-300" : isRaja ? "border-emerald-300" : "border-soft";
  return (
    <article className={`rounded-2xl overflow-hidden border ${border}`} data-testid={`rtn-${ins.planet.toLowerCase()}`}>
      <div className={`p-6 bg-gradient-to-br ${bg}`}>
        <div className="flex items-baseline gap-3 flex-wrap">
          {isTwin && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-300 text-amber-950">TWIN ACTIVATION</span>}
          {isDusthana && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-300 text-rose-950">DUSTHANA</span>}
          {isRaja && !isTwin && !isDusthana && <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-300 text-emerald-950">RAJA-YOGA</span>}
          {ins.rtn_house_tags.map((t) => (
            <span key={t} className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${TAG_TINT[t]?.bg || "bg-slate-200"} ${TAG_TINT[t]?.text || "text-slate-800"} capitalize`}>{t}</span>
          ))}
          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{ins.planet}</h3>
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted">Natal D1</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{ins.natal_sign} <span className="text-muted">(H{ins.natal_house})</span></div>
          </div>
          <div>
            <div className="text-muted">In D9</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{ins.d9_sign}</div>
          </div>
          <div>
            <div className="text-muted">→ RTN house</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">H{ins.rtn_house}</div>
          </div>
          <div>
            <div className="text-muted">Theme</div>
            <div className="font-medium text-[#3F2D1B] mt-0.5">{ins.rtn_house_title}</div>
          </div>
        </div>
        <p className="text-sm mt-4 font-light leading-snug text-[#3F2D1B]">{ins.dasha_insight}</p>
      </div>
    </article>
  );
}

export default function RashiTulyaNavamsaPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  usePrefillBirthDetails(setForm, setPlaceQuery);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/rashi-tulya-navamsa/compute", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='rtn-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to compute RTN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Stack size={14} weight="duotone" /> Rashi Tulya Navamsa · superimposed chart
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Your D9 planets, overlaid on your <span className="text-gold">D1 chart</span>
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Take each planet's D9 (Navamsa) sign and overlay it on your D1 lagna. Whichever D1 house that D9-sign becomes is the planet's <b>RTN house</b> — the additional house theme it activates during its mahadasha and antardashas. So if your <b>D9 Saturn</b> sits in <b>Aries</b> and Aries is your 5th house, Saturn's dasha gives you 5th-house results (children, romance, speculation, intelligence) alongside whatever its natal D1 placement promises.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="rtn-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="rtn-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="rtn-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="rtn-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`rtn-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="rtn-submit">
            {loading ? "Superimposing charts…" : <>Reveal my RTN chart <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="rtn-result">
            {/* === 3-UP CHARTS === */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="rounded-2xl p-4 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="d1-card">
                <div className="label-eyebrow text-[#7C2D12]">D1 · Rasi (Lagna {data.d1_lagna_rashi})</div>
                <div className="mt-3 flex justify-center">
                  <KundaliChart kundali={data.d1_chart} size={280} />
                </div>
                <p className="text-[11px] text-muted mt-3 text-center font-light">Natal planets in their birth signs.</p>
              </div>
              <div className="rounded-2xl p-4 bg-gradient-to-br from-[#F0F4FF] to-[#E0EAFF] border border-indigo-200" data-testid="d9-card">
                <div className="label-eyebrow text-indigo-900">D9 · Navamsa</div>
                <div className="mt-3 flex justify-center">
                  <KundaliChart kundali={data.d9_chart} size={280} />
                </div>
                <p className="text-[11px] text-muted mt-3 text-center font-light">Soul-level chart — planets in their navamsa signs.</p>
              </div>
              <div className="rounded-2xl p-4 bg-gradient-to-br from-[#F5EDFE] to-[#E9DDFB] border border-violet-300" data-testid="rtn-card">
                <div className="label-eyebrow text-violet-900 flex items-center gap-1.5">RTN · superimposed <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-300 text-violet-950">KEY</span></div>
                <div className="mt-3 flex justify-center">
                  <KundaliChart kundali={data.rtn_chart} size={280} />
                </div>
                <p className="text-[11px] text-muted mt-3 text-center font-light">D9 planets overlaid on D1 lagna — these are the RTN houses.</p>
              </div>
            </section>

            {/* === SIGNATURES SUMMARY === */}
            <section className="rounded-2xl p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border border-amber-200" data-testid="signatures">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Sparkle size={14} weight="duotone" /> Key signatures in your superimposition
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="rounded-xl p-4 bg-white border border-amber-200">
                  <div className="label-eyebrow text-amber-900">Twin Activations</div>
                  <p className="font-medium text-[#3F2D1B] mt-2">
                    {data.signatures.twin_activations.length > 0 ? data.signatures.twin_activations.join(" · ") : "— none —"}
                  </p>
                  <p className="text-xs text-muted mt-2 font-light">RTN house matches natal D1 house. Double-force results during these planets' dashas.</p>
                </div>
                <div className="rounded-xl p-4 bg-white border border-emerald-200">
                  <div className="label-eyebrow text-emerald-900">Raja-Yoga Activations</div>
                  <p className="font-medium text-[#3F2D1B] mt-2">
                    {data.signatures.raja_yoga_activations.length > 0 ? data.signatures.raja_yoga_activations.join(" · ") : "— none —"}
                  </p>
                  <p className="text-xs text-muted mt-2 font-light">D9 superimposed on a kendra/trikona house — these dashas elevate you.</p>
                </div>
                <div className="rounded-xl p-4 bg-white border border-rose-200">
                  <div className="label-eyebrow text-rose-900">Dusthana Activations</div>
                  <p className="font-medium text-[#3F2D1B] mt-2">
                    {data.signatures.dusthana_activations.length > 0 ? data.signatures.dusthana_activations.join(" · ") : "— none —"}
                  </p>
                  <p className="text-xs text-muted mt-2 font-light">D9 superimposed on H6/8/12. These dashas test before they reward.</p>
                </div>
              </div>
            </section>

            {/* === PER-PLANET INSIGHTS === */}
            <PaywallGate
              tool="rashi_tulya_navamsa"
              toolLabel="Rashi Tulya Navamsa · D9 over D1"
              birthDetails={{ date: form.date, time: form.time, place: form.place || placeQuery, timezone: form.timezone }}
              report={data}
            >
              <div className="space-y-10">
              <section className="space-y-5" data-testid="planet-cards">
              <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                <Crown size={14} weight="duotone" /> Per-planet dasha activation
              </div>
              {data.planet_insights.map((ins) => <InsightCard key={ins.planet} ins={ins} />)}
            </section>

            {/* === HOUSE OVERLAY TABLE === */}
            <section className="rounded-2xl p-6 bg-white border border-soft" data-testid="house-overlay">
              <div className="label-eyebrow text-muted flex items-center gap-2">
                <Eye size={14} weight="duotone" /> House-by-house overlay
              </div>
              <p className="text-xs text-muted mt-1 font-light">Each row shows one D1 house, the sign sitting there, your natal planets in it, and the D9 planets superimposed on it.</p>
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-soft text-xs text-muted">
                      <th className="text-left py-2 px-2">House</th>
                      <th className="text-left py-2 px-2">Sign</th>
                      <th className="text-left py-2 px-2">Natal D1 planets</th>
                      <th className="text-left py-2 px-2">D9 → RTN planets</th>
                      <th className="text-left py-2 px-2">Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.house_overlay.map((h) => (
                      <tr key={h.house} className="border-b border-soft/40">
                        <td className="py-2 px-2 font-medium text-[#3F2D1B]">H{h.house}</td>
                        <td className="py-2 px-2 text-[#3F2D1B]">{h.sign}</td>
                        <td className="py-2 px-2 text-[#3F2D1B]">{h.natal_planets.length > 0 ? h.natal_planets.join(", ") : <span className="text-muted">—</span>}</td>
                        <td className="py-2 px-2 text-violet-900 font-medium">{h.rtn_planets.length > 0 ? h.rtn_planets.join(", ") : <span className="text-muted font-normal">—</span>}</td>
                        <td className="py-2 px-2 text-xs">
                          {h.tags.map((t) => (
                            <span key={t} className={`inline-block px-1.5 py-0.5 mr-1 rounded ${TAG_TINT[t]?.bg || "bg-slate-200"} ${TAG_TINT[t]?.text || "text-slate-800"} capitalize`}>{t}</span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Jaimini"
              pitch="Get a 1:1 read on how each of your upcoming dashas will play out — RTN + natal placements + dasha bhukti timeline — with a Jaimini specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
